import { useMutation, useQueryClient } from '@tanstack/react-query';

import { apiClient } from '@/lib/api/client';
import { useTenantStore } from '@/stores/tenantStore';

import { type QuestionnaireDto, questionnaireDtoSchema } from '../schemas/questionnaireSchemas';
import { questionnaireKeys } from './questionnaireKeys';

/**
 * Copies a questionnaire, sections and fields included.
 *
 * One call. The copy is assembled server-side (see DuplicateQuestionnaireCommand) rather than by
 * creating a shell here and Saving the source's tree into it — that version needs the whole tree
 * round-tripped through the browser with every id nulled by hand, and a single missed id MOVES that
 * row onto the copy and empties the original.
 */
export function useDuplicateQuestionnaire() {
  const qc = useQueryClient();
  const tenantId = useTenantStore((s) => s.tenantId);

  return useMutation({
    mutationFn: async (questionnaireId: string): Promise<QuestionnaireDto> => {
      const raw = await apiClient
        .post(`api/v1/t/${tenantId}/questionnaires/${questionnaireId}/duplicate`)
        .json();
      return questionnaireDtoSchema.parse(raw);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: questionnaireKeys.lists() });
    },
  });
}
